import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  generateGrowCrmSecret,
  getChannelBotSettings,
  getGrowCrmConfig,
  testGrowCrmConfig,
  updateChannelBotSetting,
  updateGrowCrmConfig,
} from '../../api/tenant'
import { useOAuthPopup } from '../../hooks/useOAuthPopup'
import ZaloZbsCard from './ZaloZbsCard'

const cardStyle     = { background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 18, marginBottom: 12 }
const inputStyle    = { width: '100%', padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: '#f5f6f8', color: '#374151', cursor: 'default', outline: 'none', boxSizing: 'border-box' }
const editInputStyle = { ...inputStyle, background: 'white', cursor: 'text' }
const labelStyle    = { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block', marginTop: 8 }
const btnPrimary    = { background: '#111', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 14 }
const btnSecondary  = { background: 'white', color: '#111', border: '1px solid #e0e3f0', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 8 }
const helpBoxStyle   = { marginTop: 12, border: '1px solid #dbeafe', borderRadius: 8, padding: 12, background: '#eff6ff' }
const fieldBoxStyle  = { border: '1px solid #e0e3f0', borderRadius: 8, padding: 12 }
const channelBotChannels = new Set(['zalo', 'facebook'])

const CHANNELS = [
  {
    key: 'zalo',
    label: 'Zalo OA',
    fields: [
      { key: 'zalo_oa_id',    label: 'Zalo OA ID' },
      { key: 'zalo_oa_token', label: 'Zalo OA Token', sensitive: true },
    ],
  },
  {
    key: 'facebook',
    label: 'Facebook Messenger',
    fields: [
      { key: 'fb_page_id',    label: 'Facebook Page ID' },
      { key: 'fb_page_token', label: 'Page Access Token', sensitive: true },
    ],
  },
]

function formFromBot(bot) {
  return {
    zalo_oa_id:      bot?.zalo_oa_id      ?? '',
    zalo_oa_token:   bot?.zalo_oa_token   ?? '',
    zalo_enabled:    bot?.zalo_enabled    !== false,
    fb_page_id:      bot?.fb_page_id      ?? '',
    fb_page_token:   bot?.fb_page_token   ?? '',
    fb_enabled:      bot?.fb_enabled      !== false,
  }
}

function normalizeChannelKey(channel) {
  const key = String(channel || '').trim().toLowerCase()
  if (key === 'fb' || key === 'messenger') return 'facebook'
  if (key === 'zalo_oa') return 'zalo'
  return key
}

function indexChannelBotSettings(settings) {
  const indexed = {}
  ;(Array.isArray(settings) ? settings : []).forEach(item => {
    const key = normalizeChannelKey(item?.channel)
    if (!channelBotChannels.has(key)) return
    indexed[key] = {
      ...item,
      channel: key,
      bot_enabled: item?.bot_enabled !== false,
      channel_enabled: typeof item?.channel_enabled === 'boolean' ? item.channel_enabled : undefined,
    }
  })
  return indexed
}

function apiErrorMessage(err, fallback) {
  const data = err?.response?.data
  const detail = data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail)) {
    const message = detail.map(item => item?.msg || item?.message).filter(Boolean).join(', ')
    if (message) return message
  }
  return data?.error || data?.message || fallback
}

function badgeStyle(tone) {
  if (tone === 'success') return { color: '#16a34a', background: '#dcfce7' }
  if (tone === 'warning') return { color: '#92400e', background: '#fef3c7' }
  if (tone === 'neutral') return { color: '#475569', background: '#f1f5f9' }
  return { color: '#1535c8', background: '#e8ecff' }
}

function StatusBadge({ tone = 'neutral', children }) {
  const colors = badgeStyle(tone)
  return (
    <span style={{ fontSize: 11, fontWeight: 600, ...colors, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  )
}

function ChannelBotSwitch({ enabled, disabled, saving, onChange, t }) {
  const label = enabled
    ? t('chatIntegrations.botAutoReplyOn', { defaultValue: 'Bot on' })
    : t('chatIntegrations.botAutoReplyOff', { defaultValue: 'Bot off' })
  const title = disabled
    ? t('chatIntegrations.botToggleDisabled', { defaultValue: 'Bot setting is not available for this channel.' })
    : t('chatIntegrations.botToggleTitle', { defaultValue: 'Toggle chatbot auto-reply for this channel' })

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
      title={title}
    >
      <span style={{ fontSize: 12, color: disabled ? '#94a3b8' : enabled ? '#1535c8' : '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {saving ? t('common.saving', { defaultValue: 'Saving...' }) : label}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!enabled)}
        style={{
          position: 'relative',
          width: 44,
          height: 24,
          borderRadius: 999,
          border: 'none',
          background: enabled ? '#1535c8' : '#cbd5e1',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.65 : 1,
          transition: 'background 0.15s ease, opacity 0.15s ease',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 3,
            left: enabled ? 23 : 3,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: 'white',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.24)',
            transition: 'left 0.15s ease',
          }}
        />
      </button>
    </div>
  )
}

function ChannelBotInfo({ setting, connected, botEnabled, loading, error, t }) {
  let message = ''
  let tone = 'info'

  if (loading) {
    message = t('chatIntegrations.botSettingsLoading', { defaultValue: 'Loading chatbot auto-reply setting...' })
    tone = 'neutral'
  } else if (error) {
    message = error
    tone = 'warning'
  } else if (!setting) {
    message = t('chatIntegrations.botSettingUnavailable', { defaultValue: 'Bot setting is not available for this channel yet.' })
    tone = 'warning'
  } else if (!connected) {
    message = t('chatIntegrations.botSettingDisconnected', { defaultValue: 'Connect this channel before managing chatbot auto-reply.' })
    tone = 'warning'
  } else if (botEnabled) {
    message = t('chatIntegrations.botEnabledHelp', { defaultValue: 'Chatbot auto-reply is enabled for this channel.' })
  } else {
    message = t('chatIntegrations.botDisabledHelp', {
      defaultValue: 'Chatbot is off for this channel. Messages still arrive in GrowChat for staff to handle.',
    })
    tone = 'warning'
  }

  const colors = tone === 'warning'
    ? { border: '#fde68a', background: '#fffbeb', title: '#92400e', body: '#78350f' }
    : tone === 'neutral'
      ? { border: '#e2e8f0', background: '#f8fafc', title: '#475569', body: '#64748b' }
      : { border: '#bfdbfe', background: '#eff6ff', title: '#1e3a8a', body: '#334155' }

  return (
    <div style={{ marginTop: 12, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12, background: colors.background }}>
      <p style={{ fontSize: 12, fontWeight: 700, color: colors.title, margin: '0 0 6px' }}>
        {t('chatIntegrations.botAutoReplyTitle', { defaultValue: 'Chatbot auto-reply' })}
      </p>
      <p style={{ fontSize: 12, color: colors.body, margin: 0, lineHeight: 1.5 }}>
        {message}
      </p>
      {setting?.disabled_reason ? (
        <p style={{ fontSize: 11, color: '#92400e', margin: '6px 0 0' }}>
          {t('chatIntegrations.disabledReason', { reason: setting.disabled_reason, defaultValue: 'Reason: {{reason}}' })}
        </p>
      ) : null}
    </div>
  )
}

function ZaloConnectButton({ tenantId, configured, onDone }) {
  const connectUrl = `https://api.growchat.ai/auth/zalo/connect?tenant_id=${tenantId}`
  const openPopup  = useOAuthPopup(connectUrl, onDone)
  const label      = configured ? 'Kết nối lại Zalo OA' : '+ Kết nối Zalo OA'

  return (
    <div>
      <button style={btnPrimary} onClick={e => { e.stopPropagation(); openPopup() }}>
        {label}
      </button>
      <p style={{ fontSize: 11, color: '#888', marginTop: 6, textAlign: 'center' }}>
        Bạn sẽ được chuyển đến trang xác thực Zalo OA
      </p>
    </div>
  )
}

function FacebookConnectButton({ tenantId, configured, onDone }) {
  const connectUrl = `https://api.growchat.ai/auth/facebook/connect?tenant_id=${tenantId}`
  const openPopup  = useOAuthPopup(connectUrl, onDone)
  const label      = configured ? 'Kết nối lại Facebook' : '+ Kết nối Facebook'

  return (
    <div>
      <button style={{ ...btnPrimary, background: '#1877f2' }} onClick={e => { e.stopPropagation(); openPopup() }}>
        {label}
      </button>
      <p style={{ fontSize: 11, color: '#888', marginTop: 6, textAlign: 'center' }}>
        Bạn sẽ được chuyển đến trang xác thực Facebook
      </p>
    </div>
  )
}

function growCrmAppUrlFromWebhook(value) {
  const raw = String(value || '').trim()
  return raw.replace(/\/api\/webhooks\/growchat\/?$/i, '')
}

function growCrmWebhookUrlFromInput(value) {
  const raw = String(value || '').trim().replace(/\/+$/, '')
  if (!raw) return ''
  if (/\/api\/webhooks\/growchat$/i.test(raw)) return raw
  return `${raw}/api/webhooks/growchat`
}

function GrowCrmIntegrationCard({ tenantId, bot, onUpdate }) {
  const [open, setOpen] = useState(false)
  const [config, setConfig] = useState(() => ({
    enabled: Boolean(bot?.growcrm_enabled),
    webhook_url: bot?.growcrm_webhook_url ?? '',
    webhook_secret_configured: Boolean(bot?.growcrm_webhook_secret_configured),
    service_api_key_configured: Boolean(bot?.growcrm_service_api_key_configured),
    last_tested_at: bot?.growcrm_last_tested_at ?? null,
    last_test_status: bot?.growcrm_last_test_status ?? null,
  }))
  const [growCrmUrl, setGrowCrmUrl] = useState(() => growCrmAppUrlFromWebhook(config.webhook_url))
  const [enabled, setEnabled] = useState(config.enabled)
  const [generated, setGenerated] = useState({})
  const [busy, setBusy] = useState('')
  const [message, setMessage] = useState('')

  const configured = enabled && config.webhook_url && config.webhook_secret_configured && config.service_api_key_configured
  const webhookEndpointPreview = growCrmWebhookUrlFromInput(growCrmUrl)

  useEffect(() => {
    if (!tenantId) return
    getGrowCrmConfig(tenantId).then(next => {
      setConfig(next)
      setGrowCrmUrl(growCrmAppUrlFromWebhook(next.webhook_url))
      setEnabled(Boolean(next.enabled))
    }).catch(() => {})
  }, [tenantId])

  async function save() {
    setBusy('save')
    setMessage('')
    try {
      const next = await updateGrowCrmConfig(tenantId, {
        enabled,
        webhook_url: webhookEndpointPreview,
      })
      setConfig(next)
      setGrowCrmUrl(growCrmAppUrlFromWebhook(next.webhook_url))
      setEnabled(Boolean(next.enabled))
      setMessage('GrowCRM connection saved.')
      onUpdate?.()
    } catch (err) {
      setMessage(err?.response?.data?.detail || err?.response?.data?.error || 'Unable to save GrowCRM connection.')
    } finally {
      setBusy('')
    }
  }

  async function generate(kind) {
    setBusy(kind)
    setMessage('')
    try {
      const next = await generateGrowCrmSecret(tenantId, kind)
      setConfig(next)
      setGenerated(current => ({ ...current, [kind]: next.secret }))
      setMessage(kind === 'webhook_secret' ? 'Webhook secret generated.' : 'Service API key generated.')
      onUpdate?.()
    } catch (err) {
      setMessage(err?.response?.data?.detail || 'Unable to generate GrowCRM key.')
    } finally {
      setBusy('')
    }
  }

  async function test() {
    setBusy('test')
    setMessage('')
    try {
      const next = await testGrowCrmConfig(tenantId)
      setConfig(next)
      setMessage('Test message delivered to GrowCRM.')
    } catch (err) {
      setMessage(err?.response?.data?.detail || 'GrowCRM test message failed.')
    } finally {
      setBusy('')
    }
  }

  async function copy(value, label = 'Copied.') {
    if (!value) return
    await navigator.clipboard.writeText(value)
    setMessage(label)
  }

  return (
    <div style={cardStyle}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>GrowCRM</p>
          {configured
            ? <span style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: 20 }}>Connected</span>
            : <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 20 }}>Setup needed</span>
          }
        </div>
        <span style={{ fontSize: 18, color: '#888', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>⌄</span>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#333', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Sync conversations to GrowCRM
          </label>

          <div style={helpBoxStyle}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#1e3a8a', margin: '0 0 6px' }}>
              What to enter here
            </p>
            <p style={{ fontSize: 12, color: '#334155', margin: 0, lineHeight: 1.5 }}>
              Enter the web address where your team opens GrowCRM, then generate the two secure keys below.
              Copy the tenant ID and keys into GrowCRM Settings -&gt; Integrations to finish the connection.
            </p>
          </div>

          <label style={labelStyle}>GrowCRM app address</label>
          <input
            style={editInputStyle}
            type="url"
            value={growCrmUrl}
            onChange={(e) => setGrowCrmUrl(e.target.value)}
            placeholder="https://crm.yourcompany.com"
          />
          <p style={{ fontSize: 11, color: '#666', margin: '6px 0 0', lineHeight: 1.5 }}>
            Example: https://crm.growiseasia.com. GrowChat will use the correct inbox endpoint automatically.
          </p>

          <button style={btnPrimary} disabled={busy === 'save' || !tenantId} onClick={save}>
            {busy === 'save' ? 'Saving...' : 'Save GrowCRM settings'}
          </button>

          <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
            <div style={fieldBoxStyle}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#111', margin: 0 }}>GrowChat tenant ID</p>
              <p style={{ fontSize: 11, color: '#666', margin: '4px 0 10px', lineHeight: 1.5 }}>
                Paste this into GrowCRM as the GrowChat tenant ID.
              </p>
              <input readOnly style={inputStyle} value={tenantId || ''} />
              <button style={btnSecondary} disabled={!tenantId} onClick={() => copy(tenantId, 'Tenant ID copied.')}>Copy tenant ID</button>
            </div>

            <div style={fieldBoxStyle}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#111', margin: 0 }}>Webhook secret</p>
              <p style={{ fontSize: 11, color: '#666', margin: '4px 0 10px', lineHeight: 1.5 }}>
                Paste this into GrowCRM as the webhook secret. You can only copy the full value right after generating it.
              </p>
              <input readOnly style={inputStyle} value={generated.webhook_secret || (config.webhook_secret_configured ? '••••••••' : '')} />
              <button style={btnSecondary} disabled={busy === 'webhook_secret'} onClick={() => generate('webhook_secret')}>
                {busy === 'webhook_secret' ? 'Generating...' : 'Generate webhook secret'}
              </button>
              {generated.webhook_secret && (
                <button style={btnSecondary} onClick={() => copy(generated.webhook_secret, 'Webhook secret copied.')}>Copy webhook secret</button>
              )}
            </div>

            <div style={fieldBoxStyle}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#111', margin: 0 }}>Service API key</p>
              <p style={{ fontSize: 11, color: '#666', margin: '4px 0 10px', lineHeight: 1.5 }}>
                Paste this into GrowCRM as the service API key so GrowCRM can read recent conversations.
              </p>
              <input readOnly style={inputStyle} value={generated.service_api_key || (config.service_api_key_configured ? '••••••••' : '')} />
              <button style={btnSecondary} disabled={busy === 'service_api_key'} onClick={() => generate('service_api_key')}>
                {busy === 'service_api_key' ? 'Generating...' : 'Generate service API key'}
              </button>
              {generated.service_api_key && (
                <button style={btnSecondary} onClick={() => copy(generated.service_api_key, 'Service API key copied.')}>Copy service API key</button>
              )}
            </div>
          </div>

          <button style={{ ...btnPrimary, background: '#1535c8' }} disabled={busy === 'test' || !configured} onClick={test}>
            {busy === 'test' ? 'Testing...' : 'Send test message to GrowCRM'}
          </button>

          {config.last_test_status && (
            <p style={{ fontSize: 11, color: config.last_test_status === 'ok' ? '#16a34a' : '#b45309', marginTop: 8 }}>
              Last test: {config.last_test_status}
            </p>
          )}

          {message && (
            <p style={{ fontSize: 12, color: message.includes('failed') || message.includes('Unable') ? '#ef4444' : '#16a34a', marginTop: 10 }}>
              {message}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default function ChatIntegrations({ bot, tenantId, onUpdate }) {
  const { t } = useTranslation()
  const [expanded, setExpanded]         = useState({})
  const [form, setForm]                 = useState(() => formFromBot(bot))
  const [zaloJustLinked, setZaloJustLinked] = useState(false)
  const [fbJustLinked, setFbJustLinked]     = useState(false)
  const [channelBotSettings, setChannelBotSettings] = useState({})
  const [botSettingsLoading, setBotSettingsLoading] = useState(false)
  const [botSettingsError, setBotSettingsError] = useState('')
  const [savingBotChannel, setSavingBotChannel] = useState('')

  useEffect(() => { setForm(formFromBot(bot)) }, [bot])

  const loadChannelBotSettings = useCallback(async () => {
    if (!tenantId) {
      setChannelBotSettings({})
      setBotSettingsError('')
      return
    }
    setBotSettingsLoading(true)
    setBotSettingsError('')
    try {
      const data = await getChannelBotSettings(tenantId)
      setChannelBotSettings(indexChannelBotSettings(data))
    } catch (err) {
      setChannelBotSettings({})
      setBotSettingsError(apiErrorMessage(
        err,
        t('chatIntegrations.botSettingsLoadFailed', { defaultValue: 'Unable to load chatbot channel settings.' })
      ))
    } finally {
      setBotSettingsLoading(false)
    }
  }, [tenantId, t])

  useEffect(() => {
    let active = true
    if (!tenantId) {
      setChannelBotSettings({})
      setBotSettingsError('')
      setBotSettingsLoading(false)
      return undefined
    }

    async function load() {
      setBotSettingsLoading(true)
      setBotSettingsError('')
      try {
        const data = await getChannelBotSettings(tenantId)
        if (!active) return
        setChannelBotSettings(indexChannelBotSettings(data))
      } catch (err) {
        if (!active) return
        setChannelBotSettings({})
        setBotSettingsError(apiErrorMessage(
          err,
          t('chatIntegrations.botSettingsLoadFailed', { defaultValue: 'Unable to load chatbot channel settings.' })
        ))
      } finally {
        if (active) setBotSettingsLoading(false)
      }
    }

    load()
    return () => { active = false }
  }, [tenantId, t])

  function isConfigured(ch) {
    if (ch.key === 'zalo' && zaloJustLinked) return true
    if (ch.key === 'facebook' && fbJustLinked) return true
    if (ch.key === 'zalo' && form.zalo_enabled === false) return false
    if (ch.key === 'facebook' && form.fb_enabled === false) return false
    return ch.fields.some(f => form[f.key] && String(form[f.key]).trim())
  }

  function toggle(chKey) {
    setExpanded(e => ({ ...e, [chKey]: !e[chKey] }))
  }

  async function handleChannelBotToggle(channel, enabled) {
    if (!tenantId || savingBotChannel) return
    const key = normalizeChannelKey(channel)
    const previous = channelBotSettings[key]
    setSavingBotChannel(key)
    setBotSettingsError('')
    setChannelBotSettings(current => ({
      ...current,
      [key]: {
        ...(current[key] || { channel: key }),
        bot_enabled: enabled,
        disabled_reason: enabled ? null : 'manual',
      },
    }))
    try {
      const next = await updateChannelBotSetting(tenantId, key, enabled)
      setChannelBotSettings(current => ({
        ...current,
        [key]: indexChannelBotSettings([next])[key] || next,
      }))
    } catch (err) {
      setChannelBotSettings(current => {
        const next = { ...current }
        if (previous) next[key] = previous
        else delete next[key]
        return next
      })
      setBotSettingsError(apiErrorMessage(
        err,
        t('chatIntegrations.botSettingsSaveFailed', { defaultValue: 'Unable to update chatbot setting.' })
      ))
    } finally {
      setSavingBotChannel('')
    }
  }

  async function handleChannelLinked(channel) {
    if (channel === 'zalo') setZaloJustLinked(true)
    if (channel === 'facebook') setFbJustLinked(true)
    onUpdate?.()
    await loadChannelBotSettings()
  }

  function readonlyDisplay(f) {
    const raw = form[f.key]
    const v   = raw == null ? '' : String(raw).trim()
    if (f.sensitive && v) return '••••••••'
    return v
  }

  return (
    <div>
      <GrowCrmIntegrationCard tenantId={tenantId} bot={bot} onUpdate={onUpdate} />
      {CHANNELS.map(ch => {
        const configured = isConfigured(ch)
        const open       = expanded[ch.key]
        const supportsChannelBot = channelBotChannels.has(ch.key)
        const botSetting = channelBotSettings[ch.key]
        const connected = typeof botSetting?.channel_enabled === 'boolean'
          ? botSetting.channel_enabled
          : configured
        const botEnabled = botSetting?.bot_enabled !== false
        const botSaving = savingBotChannel === ch.key
        const botSwitchDisabled = botSettingsLoading || botSaving || !botSetting || !connected
        return (
          <div key={ch.key} style={cardStyle}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer' }}
              onClick={() => toggle(ch.key)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0, marginRight: 'auto' }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{ch.label}</p>
                {connected
                  ? <span style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: 20 }}>
                      {t('chatIntegrations.connected')}
                    </span>
                  : <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 20 }}>
                      Chưa kết nối
                    </span>
                }
                {supportsChannelBot && connected && botSetting && !botEnabled
                  ? <StatusBadge tone="warning">{t('chatIntegrations.channelActiveBotOff', { defaultValue: 'Channel active · Bot off' })}</StatusBadge>
                  : null
                }
                {supportsChannelBot ? (
                  <ChannelBotSwitch
                    enabled={botEnabled}
                    disabled={botSwitchDisabled}
                    saving={botSaving}
                    onChange={(enabled) => handleChannelBotToggle(ch.key, enabled)}
                    t={t}
                  />
                ) : null}
              </div>
              <span style={{ fontSize: 18, color: '#888', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>⌄</span>
            </div>

            {open && (
              <div style={{ marginTop: 14 }}>
                {supportsChannelBot ? (
                  <ChannelBotInfo
                    setting={botSetting}
                    connected={connected}
                    botEnabled={botEnabled}
                    loading={botSettingsLoading}
                    error={botSettingsError}
                    t={t}
                  />
                ) : null}

                {ch.fields.map(f => (
                  <div key={f.key}>
                    <label style={labelStyle}>{f.label}</label>
                    <input readOnly style={inputStyle} type="text" value={readonlyDisplay(f)} />
                  </div>
                ))}

                {ch.key === 'zalo' && tenantId && (
                  <ZaloConnectButton
                    tenantId={tenantId}
                    configured={configured}
                    onDone={() => handleChannelLinked('zalo')}
                  />
                )}

                {ch.key === 'facebook' && tenantId && (
                  <FacebookConnectButton
                    tenantId={tenantId}
                    configured={configured}
                    onDone={() => handleChannelLinked('facebook')}
                  />
                )}
              </div>
            )}
          </div>
        )
      })}
      <ZaloZbsCard tenantId={tenantId} zbsEnabled={bot?.zbs_enabled} />
    </div>
  )
}
